import { useParams, Link, Navigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AIChat from "@/components/AIChat";
import SectionReveal from "@/components/SectionReveal";
import SEO, { SITE } from "@/components/SEO";
import { getCategory, getPostsByCategory, CATEGORIES } from "@/blog/posts";
import { BlogCard } from "@/pages/BlogIndex";

const BlogCategory = () => {
  const { category } = useParams();
  const cat = getCategory(category);
  if (!cat) return <Navigate to="/blog" replace />;
  const items = getPostsByCategory(category);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${cat.name} — The China Explorer Journal`,
    description: cat.description,
    url: `${SITE.url}/blog/${cat.slug}`,
    hasPart: items.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      url: `${SITE.url}/blog/${p.category}/${p.slug}`,
      datePublished: p.date,
    })),
  };

  return (
    <div className="App min-h-screen bg-[#F7F5F0] text-[#121212] font-body antialiased">
      <SEO
        title={`${cat.name} — Insights from The China Explorer`}
        description={cat.description}
        path={`/blog/${cat.slug}`}
        keywords={[cat.name.toLowerCase(), "china travel", "the china explorer", "china blog"]}
        jsonLd={jsonLd}
      />
      <Header />

      <section className="pt-32 md:pt-40 pb-16 md:pb-20" style={{ background: cat.color, color: "#fff" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/blog" data-testid="back-to-blog" className="text-xs uppercase tracking-[0.25em] text-white/70 hover:text-white">
            ← All articles
          </Link>
          <p className="mt-6 text-xs md:text-sm uppercase tracking-[0.3em] text-white/70 font-semibold mb-4">✦ Category</p>
          <h1 className="font-heading text-4xl md:text-6xl tracking-tight leading-[1.05] max-w-4xl">{cat.name}</h1>
          <p className="mt-6 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">{cat.description}</p>
        </div>
      </section>

      <section className="bg-[#EAE6DF] py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap gap-2.5">
          {CATEGORIES.map((c) => (
            <Link
              key={c.slug}
              to={`/blog/${c.slug}`}
              className={`px-4 py-2 text-xs md:text-sm uppercase tracking-[0.15em] font-semibold border transition-all ${
                c.slug === cat.slug ? "text-white" : "text-[#121212] hover:text-white"
              }`}
              style={{
                borderRadius: 9999,
                background: c.slug === cat.slug ? c.color : "transparent",
                borderColor: c.slug === cat.slug ? c.color : "rgba(18,18,18,0.15)",
              }}
            >
              {c.name}
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-[#F7F5F0] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {items.length === 0 ? (
            <p className="text-[#4A4A4A]">No articles in this category yet — check back soon.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {items.map((p, i) => (
                <BlogCard key={p.slug} post={p} delay={i * 0.05} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
      <AIChat />
    </div>
  );
};

export default BlogCategory;
